import { useCallback, useEffect, useState } from 'react'
import { PatientManagersService } from '../client'
import { PatientManagerIndexDTO } from '../client/models/PatientManagerIndexDTO'

export interface PatientManagersQueryParams {
    page?: number
    limit?: number
    userId?: string | null
    email?: string | null
    name?: string | null
    active?: boolean | null
    sort?: string | null
    search?: string | null
}

export interface PatientManagerQueryParams {
    patientManagerId: string
    includeInactive?: boolean
}

/**
 * Custom hook for listing patient managers
 */
export const usePatientManagers = (params: PatientManagersQueryParams = {}) => {
    const [patientManagers, setPatientManagers] = useState<PatientManagerIndexDTO[]>([])
    const [pagination, setPagination] = useState<any>(null)
    const [totalCount, setTotalCount] = useState<number>(0)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const fetchPatientManagers = useCallback(async () => {
        setLoading(true)
        setError(null)

        try {
            const response = await PatientManagersService.listPatientManagersPatientManagersGet(params)
            setPatientManagers(response.data || [])
            setPagination(response.meta)
            setTotalCount(response.meta?.totalItems || 0)
        } catch (err) {
            setError('Failed to fetch patient managers')
            console.error('Failed to fetch patient managers:', err)
        } finally {
            setLoading(false)
        }
    }, [params])

    useEffect(() => {
        fetchPatientManagers()
    }, [fetchPatientManagers])
    return {
        patientManagers,
        pagination,
        totalCount,
        loading,
        error,
        refetch: fetchPatientManagers
    }
}

/**
 * Custom hook for getting a single patient manager
 */
export const usePatientManager = (params: PatientManagerQueryParams) => {
    const [patientManager, setPatientManager] = useState<PatientManagerIndexDTO | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const fetchPatientManager = useCallback(async () => {
        if (!params.patientManagerId) {
            setPatientManager(null)
            return
        }

        setLoading(true)
        setError(null)

        try {
            const response = await PatientManagersService.getPatientManagerPatientManagersPatientManagerIdGet(params)
            setPatientManager(response)
        } catch (err) {
            setError('Failed to fetch patient manager')
            console.error('Failed to fetch patient manager:', err)
        } finally {
            setLoading(false)
        }
    }, [params])

    useEffect(() => {
        fetchPatientManager()
    }, [fetchPatientManager])

    return {
        patientManager,
        loading,
        error,
        refetch: fetchPatientManager
    }
}
